
/*
 * VNCcontact+ : A new level of contact management
 * Copyright (C) 2015-2020 VNC – Virtual Network Consult AG (info@vnc.biz)
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, version 3 of the License.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. Look for COPYING file in the top folder.
 * If not, see http://www.gnu.org/licenses/.
 */

import { OnInit, OnDestroy, ChangeDetectionStrategy, Component, ChangeDetectorRef, ViewChild, ElementRef, NgZone } from "@angular/core";
import { MatDialogRef, MatDialog } from "@angular/material/dialog";
import { Broadcaster } from "src/app/common/providers/broadcaster.service";
import { BroadcastKeys } from "src/app/common/enums/broadcast.enum";
import { Observable, Subject } from "rxjs";
import { takeUntil, take } from "rxjs/operators";
import { CreateContactGroupDailogComponent } from "src/app/contacts/components/create-contact-group-dialog/create-contact-group-dialog.component";
import { Contact } from "src/app/common/models";
import { ContactRepository } from "src/app/contacts/repository/contact.repository";
import { Store } from "@ngrx/store";
import { ContactRootState, getContacts, getIsContactsLoading, getGroupContacts, getIsGroupContactsLoading } from "src/app/contacts/store";
import { getFederatedApps, RootState, getOnlineStatus } from "src/app/reducers";
import { Router } from "@angular/router";
import { ContactService } from "src/app/common/service/contact-service";
import { ToastService } from "src/app/common/service/tost.service";
import { MobileContactGroupDetailDialogComponent } from "../mobile-contact-group-detail-dialog/mobile-contact-group-detail-dialog.component";
import { ConfirmationDialogComponent } from "../confirmation-dialog/confirmation-dialog.component";
import { ConfirmDialogType } from "src/app/common/models/dialog.model";
import { SetRoutingType, SetRouteId } from "src/app/actions/app";
import { AppConstants } from "src/app/common/utils/app-constants";
import { DatabaseService } from "src/app/common/service/db/database.service";
import { CommonUtil } from "src/app/common/utils/common.utils";

@Component({
    selector: "vp-contact-group-dialog",
    templateUrl: "./contact-group-dialog.component.html",
    changeDetection: ChangeDetectionStrategy.OnPush
})

export class ContactGroupDialogComponent implements OnDestroy, OnInit {

    private isAlive$ = new Subject<boolean>();
    isSearchView: boolean = false;
    contacts: Contact[] = [];
    isLoading: boolean = false;
    searchText: string = "";
    allChecked: boolean = false;
    totalSelectedContact: number = 0;
    checkedContact: Contact[] = [];
    isShowSendEmailOption: boolean = true;
    isShowTalkOptions: boolean = true;
    isShowTaskOption: boolean = true;
    fedrateApps: any;
    isOnline: boolean = true;
    isOnMobile: boolean;
    isShowCalendarOption: boolean = true;
    @ViewChild("searchTextInput", {static: false}) searchTextInput: ElementRef;

    constructor(
        private dialogRef: MatDialogRef<ContactGroupDialogComponent>,
        private broadcaster: Broadcaster,
        private changeDetectionRef: ChangeDetectorRef,
        private matDialog: MatDialog,
        private contactRepository: ContactRepository,
        private appStore: Store<ContactRootState | RootState>,
        private router: Router,
        private contactService: ContactService,
        private toastService: ToastService,
        private databaseService: DatabaseService,
        private ngZone: NgZone
    ) {
        this.isOnMobile = CommonUtil.isOnMobileDevice();
        this.contactRepository.getAllContactGroup();
        this.appStore.dispatch(new SetRoutingType(AppConstants.CONTACTS));
        this.appStore.dispatch(new SetRouteId(AppConstants.CONTACT_GROUP));
    }

    close(): void {
        this.broadcaster.broadcast(BroadcastKeys.SHOW_HAMBURGER_MENU);
        this.dialogRef.close();
    }

    ngOnInit() {
        this.broadcaster.on<any>(BroadcastKeys.HIDE_CONTACT_GROUP_DIALOG).pipe(takeUntil(this.isAlive$)).subscribe(presence => {
            this.ngZone.run(() => {
                this.close();
            });
        });

        this.broadcaster.on<any>(BroadcastKeys.RESET_CONTACT_LIST).pipe(takeUntil(this.isAlive$)).subscribe(res => {
            this.ngZone.run(() => {
                this.resetSelection();
            });
        });

        this.appStore.select(getGroupContacts).pipe(takeUntil(this.isAlive$)).subscribe((contacts: Contact[]) => {
            if (!!contacts && contacts.length > 0) {
                // const withoutDeleteContact = contacts.filter(c => !c.deleted_at);
                const withoutDeleteContact = contacts.filter(c => !c.deleted_at && (c.fullName !== AppConstants.FAVOURITE_LIST_NAME) && (c.fullName !== AppConstants.HIDDEN_REMOVED_LIST_NAME));
                console.log("[getContacts]: ", withoutDeleteContact);
                this.contacts = withoutDeleteContact;
                this.changeDetectionRef.markForCheck();
            } else if (!!contacts && contacts.length === 0) {
                this.contacts = [];
                this.changeDetectionRef.markForCheck();
            }
        });
        this.appStore.select(getOnlineStatus).subscribe(res => {
            this.isOnline = res;
        });


        this.appStore.select(getIsGroupContactsLoading).pipe(takeUntil(this.isAlive$)).subscribe(res => {
            this.isLoading = res;
            this.changeDetectionRef.markForCheck();
        });

        this.appStore.select(getFederatedApps).pipe(takeUntil(this.isAlive$)).subscribe(apps => {
            console.log("[getFederatedApps]", apps);
            this.fedrateApps = apps;
            const isMailAppAvailable = this.fedrateApps.filter(fa => fa.name === "vncmail")[0];
            if (!!isMailAppAvailable) {
                this.isShowSendEmailOption = true;
            } else {
                this.isShowSendEmailOption = false;
            }
            const isTalkAppAvailable = this.fedrateApps.filter(fa => fa.name === "vnctalk")[0];
            if (!!isTalkAppAvailable) {
                this.isShowTalkOptions = true;
            } else {
                this.isShowTalkOptions = false;
            }
            const isTaskAppAvailable = this.fedrateApps.filter(fa => fa.name === "vnctask")[0];
            if (!!isTaskAppAvailable) {
              this.isShowTaskOption = true;
            } else {
              this.isShowTaskOption = false;
            }
            const isCalendarAppAvailable = this.fedrateApps.filter(fa => fa.name === "vnccalendar")[0];
            if (!!isCalendarAppAvailable) {
                this.isShowCalendarOption = true;
            } else {
                this.isShowCalendarOption = false;
            }
            this.changeDetectionRef.markForCheck();
          });
    }

    ngOnDestroy() {
        this.contactRepository.setRouteTypeBasedonURL();
        this.isAlive$.next(false);
        this.isAlive$.complete();
    }

    openSearchView(value: boolean): void {
        if (value) {
            setTimeout(() => {
                this.searchTextInput.nativeElement.focus();
            }, 200);
        }
        this.isSearchView = value;
        this.changeDetectionRef.markForCheck();
    }

    createGroupContactDialog(): void {
        this.matDialog.open(CreateContactGroupDailogComponent, {
            maxWidth: "100%",
            autoFocus: false,
            disableClose: true,
            panelClass: "create_contactgroup_dialog"
        });
    }

    getAllContactGroup(): void {
        this.contactRepository.getAllContactGroup();
    }

    scrollMoreContacts(event: any): void {
        if (event && event.target && !this.isLoading) {
            if (Math.ceil(event.target.scrollTop) + event.target.offsetHeight >= event.target.scrollHeight) {
              this.contactRepository.loadMoreContactGroup();
            }
        }
    }

    openGroupContactDetail(contact: Contact): void {
        if (this.totalSelectedContact > 0) {
            this.selectContactItem(contact);
            return;
        }
        this.isLoading = true;
        this.changeDetectionRef.markForCheck();
        if (this.isOnline) {
            this.contactService.getContactGroupById(contact.id).pipe(take(1)).subscribe(res => {
                this.isLoading = false;
                this.changeDetectionRef.markForCheck();
                if (!!res && res.contact_group){
                    const group = res.contact_group;
                    const memberContacts: Contact[] = this.contactRepository.mapContacts(group.contacts);
                    this.matDialog.open(MobileContactGroupDetailDialogComponent, {
                        maxWidth: "100%",
                        autoFocus: false,
                        panelClass: "mobile-group-detail-dialog",
                        disableClose: true,
                        data: {isEdit: true, contact: contact, memberContacts: memberContacts}
                    });
                }
            }, error => {
                this.isLoading = false;
                this.changeDetectionRef.markForCheck();
                this.toastService.showPlainMessage(error);
            });
        } else {
            this.databaseService.getContactGroup(contact.id).pipe(take(1)).subscribe(res => {
                this.isLoading = false;
                this.changeDetectionRef.markForCheck();
                if (!!res) {
                    console.log("contact-group-dialog getContactGroup db res: ", res);
                    const group = res;
                    const memberContacts: Contact[] = this.contactRepository.mapContacts(group.contacts);
                    this.matDialog.open(MobileContactGroupDetailDialogComponent, {
                        maxWidth: "100%",
                        autoFocus: false,
                        panelClass: "mobile-group-detail-dialog",
                        disableClose: true,
                        data: { isEdit: true, contact: contact, memberContacts: memberContacts }
                    });
                }
            }, error => {
                this.isLoading = false;
                this.changeDetectionRef.markForCheck();
                this.toastService.showPlainMessage(error);
            });
        }
    }

    emptySearchText(): void {
        this.searchText = "";
        this.changeDetectionRef.markForCheck();
    }

    selectContactItem(contact: Contact): void {
        contact.isChecked = !contact.isChecked;
        this.markMultiple(contact, contact.isChecked);
    }

    markAll(allChecked) {
        this.checkedContact = [];
        this.contacts.forEach(contact => {
            contact.isChecked = allChecked;
            if (allChecked) {
                this.checkedContact.push(contact);
            }
            this.setMarkAllStatus(allChecked);
        });
        this.totalSelectedContact = this.checkedContact.length;
        this.changeDetectionRef.markForCheck();
    }

    setMarkAllStatus(isChecked: boolean) {
        this.allChecked = isChecked;
        this.changeDetectionRef.markForCheck();
    }

    markMultiple(contact, value) {
        if (this.checkedContact && this.checkedContact.includes(contact)) {
            if (!value) {
                this.checkedContact.splice(
                    this.checkedContact.indexOf(contact),
                    1
                );
            }
        } else {
            if (value) {
                this.checkedContact.push(contact);
            }
        }
        let checkedContactsLength = this.checkedContact.length;
        if (checkedContactsLength === this.contacts.length) {
            this.setMarkAllStatus(true);
        } else {
            this.setMarkAllStatus(false);
        }
        this.totalSelectedContact = this.checkedContact.length;
        this.changeDetectionRef.markForCheck();
    }


    floatingOperation(operation: string, value: any): void {
        switch (operation) {
            case "delete":
                this.deleteContactGroup();
                break;
            case "audioCall":
                this.audioCall();
                break;
            case "videoCall":
                this.videoCall();
                break;
            case "chat":
                this.chatConveration();
                break;
            case "email":
                this.sendEmail();
                break;
            case "createTask":
                this.createTask();
                break;
            case "broadcast":
                this.startBroadcast();
                break;
            case "shareScreen":
                this.shareScreen();
                break;
            case "event":
                this.composeEvent();
                break;
        }
    }

    resetSelection(): void {
        this.totalSelectedContact = 0;
        this.markAll(false);
        this.changeDetectionRef.markForCheck();
    }

    deleteContactGroup(): void {
        const globalContacts = this.checkedContact.filter(c => c.is_global);
        if (!!globalContacts && globalContacts.length === this.checkedContact.length) {
            this.toastService.show("GLOBAL_SELECTED_CONTACT_ERROR");
            return;
        }
        const dlg = this.matDialog.open(ConfirmationDialogComponent, {
            maxWidth: "100%",
            autoFocus: false,
            panelClass: "confirm_contactplus_dialog",
            data : { dialogType: ConfirmDialogType.DELETE_CONTACT, message_title: "DELETE", message_body: "CONFIRM_DELETE_CONTACT_GROUP"}
        });
        dlg.afterClosed().pipe(take(1)).subscribe(res => {
            if (!!res && res.confirmation) {
                if (res.confirmation === "yes") {
                    console.log("Delete contact group", this.checkedContact);
                    this.contactRepository.deleteContactGroup(this.checkedContact);
                }
            }
        });
    }

    audioCall(): void {
        this.getContactGroupMembers(this.checkedContact[0]).pipe(take(1)).subscribe((contacts: Contact[]) => {
            if (!!contacts && contacts.length > 0) {
                this.contactRepository.makeTalkAudioChatVideoOperation(contacts, "audio-call", "group-audio");
            }
        });
    }

    videoCall(): void {
        this.getContactGroupMembers(this.checkedContact[0]).pipe(take(1)).subscribe((contacts: Contact[]) => {
            if (!!contacts && contacts.length > 0) {
                this.contactRepository.makeTalkAudioChatVideoOperation(contacts, "video-call", "group-audio");
            }
        });
    }

    chatConveration(): void {
        this.getContactGroupMembers(this.checkedContact[0]).pipe(take(1)).subscribe((contacts: Contact[]) => {
            if (!!contacts && contacts.length > 0) {
                this.contactRepository.makeTalkAudioChatVideoOperation(contacts, "start-chat", "group-audio");
            }
        });
    }

    shareScreen(): void {
        this.getContactGroupMembers(this.checkedContact[0]).pipe(take(1)).subscribe((contacts: Contact[]) => {
            if (!!contacts && contacts.length > 0) {
                this.contactRepository.makeTalkAudioChatVideoOperation(contacts, "screen-share", "group-screen");
            }
        });
    }

    sendEmail(): void {
        this.getContactGroupMembers(this.checkedContact[0]).pipe(take(1)).subscribe((contacts: Contact[]) => {
            if (!!contacts && contacts.length > 0) {
                this.contactRepository.sendEmail(contacts);
            }
        });
    }

    starContacts(): void {
        this.getContactGroupMembers(this.checkedContact[0]).pipe(take(1)).subscribe((contacts: Contact[]) => {
            if (!!contacts && contacts.length > 0) {
                this.contactRepository.markContactFavorite(true, contacts);
            }
        });
    }

    startBroadcast(): void {
        this.getContactGroupMembers(this.checkedContact[0]).pipe(take(1)).subscribe((contacts: Contact[]) => {
            if (!!contacts && contacts.length > 0) {
                this.contactRepository.makeBroadcastOperation(contacts);
            }
        });
    }

    composeEvent(): void {
        this.getContactGroupMembers(this.checkedContact[0]).pipe(take(1)).subscribe((contacts: Contact[]) => {
            if (!!contacts && contacts.length > 0) {
                this.contactRepository.composeCalendarEvent(contacts);
            }
        });
    }

    createTask(): void {
        this.getContactGroupMembers(this.checkedContact[0]).pipe(take(1)).subscribe((contacts: Contact[]) => {
            if (!!contacts && contacts.length > 0) {
                this.contactRepository.createTask(contacts);
            }
        });
    }

    getContactGroupMembers(contact: Contact): Observable<Contact[]> {
        const contacts = new Subject<Contact[]>();
        if (this.isOnline) {
            this.contactService.getContactGroupById(contact.id).pipe(take(1)).subscribe(res => {
                if (!!res && res.contact_group){
                    const group = res.contact_group;
                    const memberContacts: Contact[] = this.contactRepository.mapContacts(group.contacts);
                    console.log("[getContactGroupMembers]: ", memberContacts);
                    contacts.next(memberContacts);
                }
            }, error => {
                contacts.next([]);
                this.toastService.showPlainMessage(error);
            });
        } else {
            this.databaseService.getContactGroup(contact.id).pipe(take(1)).subscribe(res => {
                if (!!res && res.contact_group) {
                    const group = res.contact_group;
                    const memberContacts: Contact[] = this.contactRepository.mapContacts(group.contacts);
                    console.log("[getContactGroupMembers]: ", memberContacts);
                    contacts.next(memberContacts);
                }
            }, error => {
                contacts.next([]);
                this.toastService.showPlainMessage(error);
            });
        }
        return contacts.asObservable();
    }

}